import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';

const STATUS_LABELS = { pending: 'معلق', accepted: 'مقبول', rejected: 'مرفوض' };
const ROWS_PER_PDF_PAGE = 28;

const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('ar-LY') : '');

const toRow = (application) => ({
    'الرقم المرجعي': application.referenceNumber || '',
    'الاسم الكامل': application.fullName || '',
    'الرقم الوطني': application.nationalNumber || '',
    'رقم الهاتف': application.phoneNumber || '',
    'المدينة': application.city || '',
    'تاريخ الميلاد': formatDate(application.birthDate),
    'العمر': calculateAge(application.birthDate),
    'الحالة': STATUS_LABELS[application.status] || application.status || '',
    'سبب الرفض': application.rejectionReason || '',
    'تاريخ التقديم': formatDate(application.submittedAt)
});

const toReferenceOnlyRow = (application) => ({
    'الرقم المرجعي': application.referenceNumber || ''
});

// Splits into at most `count` roughly-equal chunks, in the given (already
// referenceNumber-sorted) order.
export const splitIntoChunks = (items, count) => {
    const safeCount = Math.max(1, Math.min(Math.floor(count) || 1, items.length || 1));
    if (items.length === 0) return [];
    const size = Math.ceil(items.length / safeCount);
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

export const buildExcelBlob = (applications, rowBuilder = toRow) => {
    const rows = applications.map(rowBuilder);
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = Object.keys(rows[0] || {}).map(() => ({ wch: 18 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'الطلبات');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([output], { type: 'application/octet-stream' });
};

const buildPageTable = (applications, title, pageNum, totalPages, rowBuilder = toRow) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-10000px';
    container.style.left = '-10000px';
    container.style.width = '1250px';
    container.style.background = '#ffffff';
    container.style.padding = '20px';
    container.style.fontFamily = "'Noto Kufi Arabic', Tahoma, Arial, sans-serif";
    container.dir = 'rtl';

    const headers = Object.keys(rowBuilder(applications[0] || {}));

    const headerCells = headers.map((h) =>
        `<th style="border:1px solid #d0d5dd;padding:8px 10px;background:#f3f4f6;font-size:13px;white-space:nowrap;">${h}</th>`
    ).join('');

    const bodyRows = applications.map((application) => {
        const row = rowBuilder(application);
        const cells = headers.map((h) =>
            `<td style="border:1px solid #d0d5dd;padding:8px 10px;font-size:12px;text-align:center;white-space:nowrap;">${row[h] ?? ''}</td>`
        ).join('');
        return `<tr>${cells}</tr>`;
    }).join('');

    container.innerHTML = `
        <div style="text-align:center;font-size:16px;font-weight:bold;margin-bottom:10px;">
            ${title} — صفحة ${pageNum} من ${totalPages}
        </div>
        <table style="width:100%;border-collapse:collapse;">
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${bodyRows}</tbody>
        </table>
    `;

    return container;
};

export const buildPdfBlob = async (applications, title, onPageRendered, rowBuilder = toRow) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageChunks = splitIntoChunks(applications, Math.ceil(applications.length / ROWS_PER_PDF_PAGE) || 1);

    for (let i = 0; i < pageChunks.length; i++) {
        const container = buildPageTable(pageChunks[i], title, i + 1, pageChunks.length, rowBuilder);
        document.body.appendChild(container);
        const canvas = await html2canvas(container, { scale: 1.5, backgroundColor: '#ffffff' });
        document.body.removeChild(container);

        // JPEG at 0.85 quality keeps the (rasterized, but always-correct)
        // Arabic text legible while keeping file size sane across many pages —
        // lossless PNG here produced multi-MB pages that don't scale to 1000+ rows.
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const imgHeight = Math.min((canvas.height * pageWidth) / canvas.width, pageHeight);

        if (i > 0) doc.addPage();
        doc.addImage(imgData, 'JPEG', 0, 0, pageWidth, imgHeight);

        if (onPageRendered) onPageRendered();
    }

    return doc.output('blob');
};

const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Generates `fileCount` files (excel or pdf) from the sorted application list
// and downloads them — a single file directly, or several zipped together.
// referenceOnly strips every column down to just the reference number.
export const generateAndDownload = async ({ applications, format, fileCount, onProgress, referenceOnly = false }) => {
    const rowBuilder = referenceOnly ? toReferenceOnlyRow : toRow;
    const chunks = splitIntoChunks(applications, fileCount);
    const extension = format === 'pdf' ? 'pdf' : 'xlsx';
    const files = [];

    let pagesRendered = 0;
    const totalPages = format === 'pdf' ? Math.ceil(applications.length / ROWS_PER_PDF_PAGE) || 1 : chunks.length;

    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const label = chunks.length > 1 ? `طلبات-التجنيد-${i + 1}-من-${chunks.length}` : 'طلبات-التجنيد';

        const blob = format === 'pdf'
            ? await buildPdfBlob(chunk, `طلبات التجنيد (${i + 1}/${chunks.length})`, () => {
                pagesRendered++;
                if (onProgress) onProgress(pagesRendered, totalPages);
            }, rowBuilder)
            : buildExcelBlob(chunk, rowBuilder);

        if (format !== 'pdf' && onProgress) {
            pagesRendered++;
            onProgress(pagesRendered, totalPages);
        }

        files.push({ name: `${label}.${extension}`, blob });
    }

    if (files.length === 1) {
        triggerDownload(files[0].blob, files[0].name);
        return;
    }

    const zip = new JSZip();
    files.forEach(({ name, blob }) => zip.file(name, blob));
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    triggerDownload(zipBlob, 'طلبات-التجنيد.zip');
};
